// Transport for the generated client. HAND-WRITTEN, and deliberately shape-free.
//
// This file knows how to send a request and how to turn a failure into an error. It knows
// nothing about practices, envelopes or problem fields — every API shape is generated into
// `src/generated/` from the OpenAPI document, so nothing here duplicates a type (doc 21 §4).
//
// Two things it does that a bare `fetch` wrapper would not:
//
//   * the access token is *asked for* per request rather than held. (H) doc 23 §11a keeps
//     tokens in memory with an HttpOnly refresh cookie and forbids `localStorage`, so the
//     application owns the token's lifetime and this client owns none of it.
//   * a non-2xx response becomes a `SarvaApiError` carrying the parsed RFC 9457 problem
//     document. Clients branch on `problem.code`, never on the status text or the message
//     (doc 22 §4).

/**
 * Values a query parameter may take. Cursors, limits and filters — never an object.
 *
 * **An array is one of them**, and leaving it out was a real defect rather than a narrowing.
 * `GET /documents` takes `match_status` as a repeated parameter, and the generated client
 * types it `MatchStatus[] | null` — which this type did not admit, so the whole package
 * stopped compiling the moment that operation was generated. A filter the API offers and
 * the client cannot express is not a filter.
 *
 * The serialisation matters as much as the type: see `buildUrl`.
 */
export type QueryValue =
  | string
  | number
  | boolean
  | readonly (string | number | boolean)[]
  | null
  | undefined;

export interface ClientConfig {
  /** Base URL of the API, e.g. `/` behind the same origin or `https://api.dev.…`. */
  baseUrl: string;
  /** The current access token, or nothing when unauthenticated. Called per request. */
  getAccessToken?:
    | (() => string | null | undefined | Promise<string | null | undefined>)
    | undefined;
  /** The active practice, sent as `X-Sarva-Practice-Id` unless a call overrides it. */
  getPracticeId?: (() => string | null | undefined) | undefined;
  /**
   * Whether a GET to this path is narrowed to the active practice with `practice_id`.
   * Defaults to every GET. See `withActivePractice` for why, and the app's client for the
   * reads that must stay tenant-wide.
   */
  narrowsToPractice?: ((path: string) => boolean) | undefined;
  /** Injected for tests and for non-browser callers. Defaults to the global `fetch`. */
  fetch?: typeof globalThis.fetch | undefined;
  /** Observe every response — for `ETag`, `Idempotency-Replayed`, or rate-limit headers. */
  onResponse?: ((response: Response) => void) | undefined;
  /** Sent on every request. `X-Request-Id` belongs here if the caller mints its own. */
  defaultHeaders?: Record<string, string> | undefined;
}

/**
 * Query parameters as an index signature, so a generated operation's own query type — an
 * anonymous object type, which TypeScript gives an implicit index signature — is assignable
 * without a cast.
 */
export type QueryParams = { readonly [key: string]: QueryValue };

// `| undefined` is spelled out on every optional member because the workspace compiles with
// `exactOptionalPropertyTypes`. The generated client passes each member unconditionally —
// `signal: input.signal` — which under that flag is "present and possibly undefined", not
// "absent". Making the distinction explicit here keeps the generator from having to emit
// conditional spreads.
export interface RequestSpec {
  method: string;
  path: string;
  query?: QueryParams | undefined;
  headers?: Record<string, string | null | undefined> | undefined;
  body?: unknown;
  signal?: AbortSignal | undefined;
}

const PROBLEM_MEDIA_TYPE = "application/problem+json";
const PRACTICE_HEADER = "X-Sarva-Practice-Id";
const NO_CONTENT = 204;

/**
 * A non-2xx response.
 *
 * `problem` is the parsed problem document, typed `unknown` here and narrowed to the
 * generated `Problem` by `isSarvaApiError`. `code` and `status` are lifted out because they
 * are what a caller branches on, and doing it once here beats doing it at every call site.
 */
export class SarvaApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly problem: unknown;

  constructor(status: number, problem: unknown, requestId: string | null) {
    const detail = readString(problem, "detail");
    const code = readString(problem, "code") ?? "UNKNOWN";
    super(detail ?? `Request failed with status ${String(status)}`);
    this.name = "SarvaApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.problem = problem;
  }
}

function readString(value: unknown, key: string): string | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = (value as Record<string, unknown>)[key];
  return typeof candidate === "string" ? candidate : null;
}

function buildUrl(baseUrl: string, path: string, query?: QueryParams): string {
  const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    // Omitted rather than sent empty: an absent filter and an empty one are different
    // requests, and the API validates what it is given.
    if (value === null || value === undefined) continue;
    // (H) An array is repeated, never joined. `String(["a","b"])` is `"a,b"`, which FastAPI
    // reads as ONE value of a list parameter — so a two-value filter silently becomes a
    // one-value filter for a status nothing matches, and the screen shows an empty table
    // rather than an error. `?k=a&k=b` is the shape the API parses.
    //
    // An empty array is skipped for the same reason a `null` is: "filter by none of them"
    // is not a request anybody means, and sending `?k=` asks the API to validate an empty
    // string against a vocabulary.
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
      continue;
    }
    search.append(key, String(value));
  }
  const suffix = search.size > 0 ? `?${search.toString()}` : "";
  return `${base}${path}${suffix}`;
}

/**
 * A read narrowed to the active practice unless the call names one itself.
 *
 * (H) The header alone does not narrow anything. The API validates `X-Sarva-Practice-Id`
 * against the caller's grants and then filters every list by its `practice_id` *query*
 * parameter only — so a list call that did not pass one returned every practice the grants
 * cover. Invisible while the review account was scoped to one practice; the day it was
 * widened to the tenant, the dermatology practice showed the family-medicine claims under its
 * own name. Doing it here, once, is what `providers.ts` was doing by hand.
 *
 * GET only: a write names its practice in its body, and the API refuses a mismatch there.
 * An operation that takes no `practice_id` ignores the extra parameter. With no practice
 * selected ("All practices") nothing is added and the read stays tenant-wide.
 */
function withActivePractice(
  config: ClientConfig,
  spec: RequestSpec,
  practiceId: string | null | undefined,
): QueryParams | undefined {
  if (spec.method.toUpperCase() !== "GET" || !practiceId) return spec.query;
  if (config.narrowsToPractice !== undefined && !config.narrowsToPractice(spec.path)) {
    return spec.query;
  }
  const named = spec.query?.practice_id;
  if (named !== null && named !== undefined) return spec.query;
  return { ...spec.query, practice_id: practiceId };
}

/** Send one request and return its parsed body, or throw `SarvaApiError`. */
export async function request<T>(config: ClientConfig, spec: RequestSpec): Promise<T> {
  const headers = new Headers(config.defaultHeaders ?? {});
  const token = await config.getAccessToken?.();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const practiceId = config.getPracticeId?.();
  if (practiceId) headers.set(PRACTICE_HEADER, practiceId);
  for (const [name, value] of Object.entries(spec.headers ?? {})) {
    if (value === null || value === undefined) continue;
    headers.set(name, value);
  }
  if (spec.body !== undefined) headers.set("Content-Type", "application/json");
  headers.set("Accept", `application/json, ${PROBLEM_MEDIA_TYPE}`);

  const send = config.fetch ?? globalThis.fetch;
  const init: RequestInit = {
    method: spec.method,
    headers,
    // (H) `include`, not `same-origin`. The session cookie is HttpOnly (doc 23 §11a) and the
    // browser is the only thing that can attach it — and `same-origin` means it attaches it
    // only when the app and the API share an origin. They do in development, where Vite
    // proxies `/api/v1`, and they do **not** on the demo estate: the app is served from
    // `*.azurestaticapps.net` and the API from `*.azurewebsites.net`. So `POST /auth/refresh`
    // went out with no cookie, answered `401`, and every page reload signed the reviewer out.
    // The old comment here said the cookie "has to be sent" and then chose the mode that does
    // not send it — true same-site, wrong the moment the deployment was not.
    //
    // Safe cross-site for two reasons that are already enforced rather than assumed: the
    // cookie is scoped `Path=/api/v1/auth`, so the browser attaches it to the auth routes and
    // to nothing else whatever this says; and `include` requires the server to answer with an
    // explicit `Access-Control-Allow-Credentials` and a named origin — a wildcard is refused
    // by construction — which `CORS_ORIGINS` is. It also needs `SameSite=None; Secure` on the
    // cookie, which is a deployment setting (`SESSION_COOKIE_SAMESITE`) rather than anything
    // this file can control.
    credentials: "include",
  };
  // Assigned rather than always present: under `exactOptionalPropertyTypes`, `body:
  // undefined` is not the same as no body, and `fetch` rejects the former.
  if (spec.body !== undefined) init.body = JSON.stringify(spec.body);
  if (spec.signal !== undefined) init.signal = spec.signal;
  const response = await send(
    buildUrl(config.baseUrl, spec.path, withActivePractice(config, spec, practiceId)),
    init,
  );
  config.onResponse?.(response);

  if (!response.ok) {
    throw new SarvaApiError(
      response.status,
      await parseBody(response),
      response.headers.get("X-Request-Id"),
    );
  }
  if (response.status === NO_CONTENT) return undefined as T;
  return (await parseBody(response)) as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A body that is not JSON did not come from this API — a proxy error page, most likely.
    // Reporting the status with a null document beats throwing a parse error over the top of
    // the real failure.
    return null;
  }
}
